import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {Heart, Menu, X, User, LogOut, Bookmark} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'

export default function Header() {
  const location = useLocation()
  const { isAuthenticated, user } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [userMenuOpen, setUserMenuOpen] = React.useState(false)

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Therapy Guide', href: '/therapy' },
    { name: 'Conditions', href: '/conditions' },
    { name: 'Self-Assessment', href: '/assessment' },
    { name: 'Resources', href: '/resources' },
    { name: 'Contact', href: '/contact' }
  ]

  const handleAuth = () => {
    if (isAuthenticated) {
      setUserMenuOpen(!userMenuOpen)
    } else {
      lumi.auth.signInWithOAuth({
        provider: 'google',
        onSuccess: () => {
          console.log('Login successful')
        },
        onError: (error) => {
          console.error('Login error:', error)
        }
      })
    }
  }

  const handleSignOut = async () => {
    await lumi.auth.signOut()
    setUserMenuOpen(false)
  }

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <Heart className="w-8 h-8 text-[#4A90E2]" />
            <span className="text-xl font-bold text-gray-900">Anxiety USA</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? 'text-[#4A90E2] border-b-2 border-[#4A90E2] pb-1'
                    : 'text-gray-700 hover:text-[#4A90E2]'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Auth Button */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={handleAuth}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#4A90E2] bg-opacity-10 text-[#4A90E2] hover:bg-opacity-20 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">{user?.email?.split('@')[0] || 'User'}</span>
                </button>
                
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                    <Link
                      to="/saved"
                      className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Bookmark className="w-4 h-4 mr-3" />
                      Saved Articles
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleAuth}
                className="px-6 py-2 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#357ABD] transition-colors"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-[#4A90E2] hover:bg-gray-100"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2 text-base font-medium rounded-md ${
                  location.pathname === item.href
                    ? 'text-[#4A90E2] bg-blue-50'
                    : 'text-gray-700 hover:text-[#4A90E2] hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="mt-4 px-4">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/saved"
                    className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md mb-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Bookmark className="w-4 h-4 mr-3" />
                    Saved Articles
                  </Link>
                  <button
                    onClick={() => {
                      handleSignOut()
                      setMobileMenuOpen(false)
                    }}
                    className="w-full flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    handleAuth()
                    setMobileMenuOpen(false)
                  }}
                  className="w-full px-6 py-2 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#357ABD] transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
